import { Alert, Box, Button, Paper, Stack, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { ArrowBack, AccessTime } from '@mui/icons-material';
import { format } from 'date-fns';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useCurrentUser } from '~/shared/hooks/useCurrentUser';
import { AvatarImage } from '~/shared/components/AvatarImage';
import { UserRole } from '~/shared/types/connect';
import { useAttorneyProfilePage } from '~/modules/connect/useAttorneyProfilePage';

function AttorneyProfilePage() {
  const navigate = useNavigate();
  const { attorneyId } = useParams();
  const { user: currentUser } = useCurrentUser();
  const {
    attorney,
    isOwnProfile,
    lastActive,
    isLoading,
    error: mattersError,
  } = useAttorneyProfilePage();

  if (!isOwnProfile && currentUser?.role !== UserRole.EXPERT) {
    return (
      <MessagePaper variant="outlined">
        <MessageTitle variant="subtitle1" fontWeight={600}>
          Experts only
        </MessageTitle>
        <MessageBody color="text.secondary">
          Only experts can view attorney profiles.
        </MessageBody>
        <Button variant="outlined" onClick={() => navigate('/matters')}>
          Back to home
        </Button>
      </MessagePaper>
    );
  }

  if (!attorneyId || !attorney) {
    return (
      <MessagePaper variant="outlined">
        <MessageTitle variant="subtitle1" fontWeight={600}>
          Attorney not found
        </MessageTitle>
        <MessageBody color="text.secondary">
          This attorney could not be found. Go to the home page to manage your
          matters and attorneys.
        </MessageBody>
        <Button variant="outlined" onClick={() => navigate('/matters')}>
          Back to home
        </Button>
      </MessagePaper>
    );
  }

  return (
    <Box>
      {!isOwnProfile && (
        <BackLink to="/matters">
          <ArrowBackIcon aria-hidden />
          Back
        </BackLink>
      )}

      <ProfilePaper variant="outlined">
        {mattersError && (
          <MattersErrorAlert severity="error" role="alert">
            {mattersError.message || 'Could not load matters. Try again later.'}
          </MattersErrorAlert>
        )}
        <ProfileRow>
          <LastActiveCell>
            {isLoading && !mattersError && !lastActive && (
              <Typography variant="caption" color="text.secondary">
                Loading matters…
              </Typography>
            )}
            {lastActive && (
              <LastActiveWrap>
                <AccessTimeIcon color="action" aria-hidden />
                <Typography variant="caption" color="text.secondary">
                  Last active {format(new Date(lastActive), 'MMM d, yyyy')}
                </Typography>
              </LastActiveWrap>
            )}
          </LastActiveCell>
          <ProfileMain>
            <ProfileAvatar
              avatarUrl={attorney.avatarUrl}
              initialsLabel={attorney.name ?? ''}
              alt={attorney.name}
            />
            <ProfileContentCol>
              <NameBlock>
                <Typography variant="h5" fontWeight={700}>
                  {attorney.name}
                </Typography>
                {attorney.firm && (
                  <CredentialsMt variant="body2" color="text.secondary">
                    {attorney.firm}
                  </CredentialsMt>
                )}
              </NameBlock>
              {!isOwnProfile && (
                <ViewJointMattersButton
                  component={Link}
                  to={`/matters?attorney=${attorney.id}`}
                  variant="outlined"
                  size="small"
                >
                  View Joint Matters
                </ViewJointMattersButton>
              )}
            </ProfileContentCol>
          </ProfileMain>
        </ProfileRow>
      </ProfilePaper>
    </Box>
  );
}

const BackLink = styled(Link)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  marginBottom: theme.spacing(2),
  color: theme.palette.text.secondary,
  textDecoration: 'underline',
  ...theme.typography.body2,
  '&:hover': { color: theme.palette.text.primary },
}));

const ArrowBackIcon = styled(ArrowBack)(() => ({ fontSize: 18 }));

const ProfilePaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
}));

const MattersErrorAlert = styled(Alert)(({ theme }) => ({
  marginBottom: theme.spacing(2),
}));

const ProfileRow = styled(Stack)(({ theme }) => ({
  flexDirection: 'column',
  gap: theme.spacing(3),
  alignItems: 'center',
  [theme.breakpoints.up('md')]: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
}));

const LastActiveCell = styled(Stack)(({ theme }) => ({
  order: -1,
  flexShrink: 0,
  width: '100%',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
  alignSelf: 'flex-start',
  [theme.breakpoints.up('md')]: {
    order: 1,
    width: 'auto',
    justifyContent: 'flex-end',
  },
}));

const LastActiveWrap = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing(0.5),
}));

const AccessTimeIcon = styled(AccessTime)(({ theme }) => ({
  fontSize: theme.typography.caption.fontSize,
}));

const ProfileMain = styled(Stack)(({ theme }) => ({
  order: 0,
  flexDirection: 'column',
  flex: '1 1 auto',
  minWidth: 0,
  gap: theme.spacing(2),
  alignItems: 'center',
  width: '100%',
  [theme.breakpoints.up('md')]: {
    flexDirection: 'row',
    alignItems: 'center',
    width: 'auto',
  },
}));

const ProfileAvatar = styled(AvatarImage)(() => ({
  width: 96,
  height: 96,
  flexShrink: 0,
}));

const ProfileContentCol = styled(Stack)(({ theme }) => ({
  minWidth: 0,
  flex: 1,
  width: '100%',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  [theme.breakpoints.up('md')]: {
    alignItems: 'flex-start',
    textAlign: 'left',
    width: 'auto',
  },
}));

const NameBlock = styled(Stack)(({ theme }) => ({
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  width: '100%',
  [theme.breakpoints.up('md')]: {
    alignItems: 'flex-start',
    textAlign: 'left',
    width: 'auto',
  },
}));

const CredentialsMt = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(0.25),
}));

const ViewJointMattersButton = styled(Button)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  textDecoration: 'none',
})) as typeof Button;

const MessagePaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  width: '100%',
  maxWidth: 480,
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(3),
  },
}));

const MessageTitle = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(1),
}));

const MessageBody = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(2),
}));

export default AttorneyProfilePage;
